import { useEffect, useMemo, useState } from 'react';
import { IJiraProject, IJiraStatus, IStatusMapping } from '@skystone/shared';
import { jiraApi } from '@/services/jira-api';
import { Badge, Button, Spinner, PageHeader, ErrorBanner } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import {
  getDefaultInternalStatus,
  getCategoryBadgeVariant,
  compareStatusMappings,
} from '@/lib/jira-status-utils';

export function AdminJiraStatusMapPage() {
  const [projects, setProjects] = useState<IJiraProject[]>([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');

  const [statuses, setStatuses] = useState<IJiraStatus[]>([]);
  const [mappings, setMappings] = useState<IStatusMapping[]>([]);
  const [initialMappings, setInitialMappings] = useState<IStatusMapping[]>([]);

  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void loadProjects();
  }, []);

  useEffect(() => {
    if (!selectedProjectKey) {
      setStatuses([]);
      setMappings([]);
      setInitialMappings([]);
      return;
    }
    void loadProjectStatuses(selectedProjectKey);
  }, [selectedProjectKey]);

  const loadProjects = async (): Promise<void> => {
    try {
      setIsLoadingProjects(true);
      setError(null);
      const data = await jiraApi.getProjects();
      setProjects(data);
    } catch {
      setError('Failed to load Jira projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadProjectStatuses = async (projectKey: string): Promise<void> => {
    try {
      setIsLoadingStatuses(true);
      setError(null);
      setSuccess(null);

      const { statuses: newStatuses, mappings: existingMappings } = await jiraApi.getProjectStatuses(projectKey);
      setStatuses(newStatuses);

      const byStatusId = new Map<string, IStatusMapping>(
        existingMappings.map((m) => [m.statusId, m])
      );

      const merged: IStatusMapping[] = newStatuses.map((s) => {
        const existing = byStatusId.get(s.id);
        return (
          existing ?? {
            projectKey,
            statusId: s.id,
            statusName: s.name,
            internalStatus: getDefaultInternalStatus(s),
          }
        );
      });

      merged.sort(compareStatusMappings);
      setMappings(merged);
      setInitialMappings(merged.map((m) => ({ ...m })));
    } catch {
      setError('Failed to load Jira project statuses');
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const initialInternalStatusById = useMemo(() => {
    return new Map(initialMappings.map((m) => [m.statusId, m.internalStatus]));
  }, [initialMappings]);

  const hasChanges = useMemo(() => {
    if (mappings.length !== initialMappings.length) return true;
    for (const mapping of mappings) {
      if (initialInternalStatusById.get(mapping.statusId) !== mapping.internalStatus) {
        return true;
      }
    }
    return false;
  }, [initialInternalStatusById, mappings, initialMappings]);

  const handleMappingChange = (statusId: string, internalStatus: IStatusMapping['internalStatus']) => {
    setMappings((prev) => {
      const next = prev.map((m) => (m.statusId === statusId ? { ...m, internalStatus } : m));
      next.sort(compareStatusMappings);
      return next;
    });
    setSuccess(null);
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedProjectKey) return;

    try {
      setIsSaving(true);
      setError(null);

      await jiraApi.saveStatusMappings(selectedProjectKey, mappings);
      setSuccess('Mappings saved successfully');
      setInitialMappings(mappings.map((m) => ({ ...m })));
    } catch {
      setError('Failed to save mappings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer variant="narrow" className="space-y-6">
      <PageHeader
        title="Map Jira Statuses"
        subtitle="Map Jira workflow statuses to internal Skystone statuses."
      />

      <div className="rounded-lg border bg-card p-6">
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-start">
          <div>
            <label
              htmlFor="jira-status-map-project"
              className="mb-2 block text-sm font-medium text-foreground"
            >
              Select Jira Project
            </label>
            <select
              id="jira-status-map-project"
              className="flex h-9 w-full max-w-md rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={selectedProjectKey}
              onChange={(e) => setSelectedProjectKey(e.target.value)}
              disabled={isLoadingProjects}
            >
              <option value="">-- Select a Jira project --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.key}>
                  {p.name} ({p.key})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3 md:border-l md:border-border md:pl-6">
            <h2 className="text-sm font-medium text-foreground">Legend</h2>
            <p className="text-sm text-muted-foreground">
              Overrides are highlighted in yellow with a "!" marker. Defaults follow the
              Jira Category. Change a dropdown and click "Save Mappings".
            </p>

            <div className="grid grid-cols-[max-content_1rem_1fr] items-center gap-x-2 gap-y-2 text-sm">
              <Badge variant="warning">To Do</Badge>
              <span className="text-muted-foreground">-&gt;</span>
              <span className="font-medium text-foreground">Backlog</span>

              <Badge variant="default">In Progress</Badge>
              <span className="text-muted-foreground">-&gt;</span>
              <span className="font-medium text-foreground">Ongoing</span>

              <Badge variant="success">Done</Badge>
              <span className="text-muted-foreground">-&gt;</span>
              <span className="font-medium text-foreground">Done</span>
            </div>
          </div>
        </div>
      </div>

      <ErrorBanner message={error} />

      {success && (
        <div className="rounded-md bg-success/10 p-4 text-sm text-success">{success}</div>
      )}

      {isLoadingStatuses && (
        <div className="flex justify-center p-8">
          <Spinner size="lg" />
        </div>
      )}

      {!isLoadingStatuses && selectedProjectKey && mappings.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Jira Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Jira Category
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Internal Mapping
                </th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping) => {
                const status = statuses.find((s) => s.id === mapping.statusId);
                const defaultInternalStatus = status ? getDefaultInternalStatus(status) : null;
                const isCustom =
                  defaultInternalStatus !== null && mapping.internalStatus !== defaultInternalStatus;

                return (
                  <tr
                    key={mapping.statusId}
                    className={`border-b border-border transition-colors ${
                      isCustom ? 'bg-warning-50 hover:bg-warning-100/60' : 'hover:bg-muted/30'
                    }`}
                  >
                    <td className="px-3 py-1.5 text-sm text-foreground">{mapping.statusName}</td>
                    <td className="px-3 py-1.5 text-sm text-foreground">
                      {status ? (
                        <Badge
                          variant={getCategoryBadgeVariant(status)}
                          className="py-0 leading-none"
                        >
                          {status.statusCategory.name}
                        </Badge>
                      ) : (
                        <span className="text-xs italic text-muted-foreground">N/A</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <select
                          className="flex h-7 w-40 rounded-md border border-input bg-background px-3 py-0.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          value={mapping.internalStatus}
                          onChange={(e) =>
                            handleMappingChange(
                              mapping.statusId,
                              e.target.value as IStatusMapping['internalStatus']
                            )
                          }
                        >
                          <option value="Backlog">Backlog</option>
                          <option value="Ongoing">Ongoing</option>
                          <option value="Done">Done</option>
                        </select>

                        {isCustom && (
                          <span
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-warning-200 bg-warning-50 text-xs font-semibold text-warning-700"
                            aria-label="Override mapping"
                            title="Override mapping"
                          >
                            !
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex justify-end border-t border-border px-3 py-2">
            <Button onClick={handleSave} disabled={isSaving || !hasChanges} size="lg">
              {isSaving ? 'Saving...' : 'Save Mappings'}
            </Button>
          </div>
        </div>
      )}

      {!isLoadingStatuses && selectedProjectKey && statuses.length === 0 && (
        <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground">
          No statuses found for this Jira project.
        </div>
      )}
    </PageContainer>
  );
}
